import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const DISMISS_THRESHOLD = 100;
const EXIT_OFFSET = 420;

const SwipeToast = ({
  isOpen,
  icon,
  title,
  description,
  ctaLabel,
  onCta,
  onDismiss,
  accentColor,
}) => {
  const { colors } = useTheme();
  const accent = accentColor || colors.accentPrimary;
  const [deltaX, setDeltaX] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [entered, setEntered] = useState(false);
  const dragStartRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setExiting(false);
      setDeltaX(0);
      const t = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(t);
    }
    setEntered(false);
  }, [isOpen]);

  const triggerDismiss = useCallback(
    (direction = 'right') => {
      if (exiting) return;
      setExiting(true);
      setDeltaX(direction === 'right' ? EXIT_OFFSET : -EXIT_OFFSET);
      window.setTimeout(() => {
        onDismiss?.();
      }, 220);
    },
    [exiting, onDismiss]
  );

  const handlePointerDown = (e) => {
    if (exiting) return;
    dragStartRef.current = e.clientX;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current || dragStartRef.current == null) return;
    const dx = e.clientX - dragStartRef.current;
    setDeltaX(Math.max(0, dx));
  };

  const handlePointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    dragStartRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (deltaX > DISMISS_THRESHOLD) {
      triggerDismiss('right');
    } else {
      setDeltaX(0);
    }
  };

  if (!isOpen) return null;

  const opacity = exiting
    ? 0
    : entered
      ? Math.max(0, 1 - Math.abs(deltaX) / 320)
      : 0;

  const transform = exiting
    ? `translateX(${deltaX}px)`
    : entered
      ? `translateX(${deltaX}px)`
      : 'translateX(60px)';

  const transition = draggingRef.current
    ? 'opacity 120ms linear'
    : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms linear';

  const node = (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 85,
        width: 340,
        maxWidth: 'calc(100vw - 40px)',
        backgroundColor: colors.bgPrimary,
        color: colors.textPrimary,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: 12,
        padding: '14px 16px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.28)',
        transform,
        opacity,
        transition,
        touchAction: 'pan-y',
        userSelect: 'none',
        cursor: draggingRef.current ? 'grabbing' : 'grab',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          triggerDismiss('right');
        }}
        aria-label="Fechar notificação"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'transparent',
          border: 'none',
          color: colors.textTertiary,
          cursor: 'pointer',
          padding: 4,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <X size={16} />
      </button>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingRight: 18 }}>
        {icon && (
          <div
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: `${accent}18`,
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.textPrimary,
              marginBottom: 4,
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
          {description && (
            <div
              style={{
                fontSize: 13,
                color: colors.textSecondary,
                lineHeight: 1.4,
                marginBottom: ctaLabel ? 10 : 0,
              }}
            >
              {description}
            </div>
          )}
          {ctaLabel && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onCta?.();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 14px',
                backgroundColor: accent,
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default SwipeToast;
